import React from 'react';

import type { Stripe } from '@stripe/stripe-js';

import { StripeInstanceContext, type StripeInstanceContextValue } from './StripeInstanceContext';

type StripeInstanceProviderProps = {
  publicKey: string | undefined;
};

export const StripeInstanceProvider: React.FC<
  React.PropsWithChildren<StripeInstanceProviderProps>
> = ({ publicKey, children }) => {
  const [stripeInstanceLoading, setStripeInstanceLoading] = React.useState(false);

  const stripeInstanceRef = React.useRef<Stripe | null>(null);

  const initStripe = React.useCallback(async () => {
    if (publicKey && !stripeInstanceRef.current) {
      try {
        setStripeInstanceLoading(true);
        const { loadStripe } = await import('@stripe/stripe-js/pure');
        stripeInstanceRef.current = await loadStripe(publicKey);
      } finally {
        setStripeInstanceLoading(false);
      }
    }
  }, [publicKey]);

  const contextValue: StripeInstanceContextValue = React.useMemo(
    () => ({
      stripeInstance: stripeInstanceRef.current,
      stripeInstanceLoading,
      initStripe,
    }),
    [stripeInstanceLoading, initStripe],
  );

  return <StripeInstanceContext value={contextValue}>{children}</StripeInstanceContext>;
};
